import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import productReducer from './slices/product-slice';
import currencyReducer from "./slices/currency-slice";
import cartReducer from "./slices/cart-slice";
import compareReducer from "./slices/compare-slice";
import wishlistReducer from "./slices/wishlist-slice";
import filterReducer from '../components/header/sub-components/filter-slice';
import authReducer from "./slices/auth-slice";
import viajanteReducer from "./slices/viajante-slice";
import cuponReducer from "./slices/cupon-slice";

const persistConfig = {
    key: "DelPlastic",
    version: 1.1,
    storage,
    blacklist: ["product", "viajante", "auth"]
}

const productPersistConfig = {
    key: "product",
    storage,
    whitelist: ["showSuggestedPrice"],
};

const viajantePersistConfig = {
    key: "viajante",
    storage,
    whitelist: ["selectedClient"],
};

// Solo user/token sobreviven a un reload. loginRateLimitedUntil/loading/error son
// estado transitorio de la UI — persistirlos hacía que un rate limit viejo (timestamp
// futuro) reapareciera al reabrir /login-register sin ninguna request nueva.
//
// stateReconciler custom: la lectura de storage (rehydrate) es async y en mobile
// puede resolver DESPUÉS de que un login ya seteó token/user frescos en memoria
// (carrera confirmada: 2026-07-06, ver HOTFIX login mobile "Consultar" en vez de
// precio). El reconciler default (autoMergeLevel1) decide si preservar un campo
// comparando "¿el reducer lo modificó durante esta acción?" — pero como el handler
// de REHYDRATE en auth-slice.js nunca toca token/user, y reasignar un valor primitivo
// IDÉNTICO no cuenta como "modificado" para Immer, ese campo siempre pierde contra el
// valor de storage aunque memoria ya tenga uno más nuevo. Acá decidimos explícito:
// token/user de MEMORIA ganan si ya existen; solo se toman de storage si memoria
// todavía no tiene nada (boot normal / reload con sesión previa).
export const authStateReconciler = (inboundState, originalState, reducedState) => ({
    ...reducedState,
    token: reducedState.token ?? inboundState?.token ?? null,
    user: reducedState.user ?? inboundState?.user ?? null,
});

const authPersistConfig = {
    key: "auth",
    storage,
    whitelist: ["user", "token"],
    stateReconciler: authStateReconciler,
};

export const rootReducer = combineReducers({
    product: persistReducer(productPersistConfig, productReducer),
    currency: currencyReducer,
    cart: cartReducer,
    compare: compareReducer,
    wishlist: wishlistReducer,
    filter: filterReducer,
    auth: persistReducer(authPersistConfig, authReducer),
    viajante: persistReducer(viajantePersistConfig, viajanteReducer),
    cupon: cuponReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                ],
            },
        }),
});

export const persistor = persistStore(store);